import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import COMMON from "common";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppSelectInput from "components/app-select-input";

export const AppOvertimeRejectReasonModal = (props, ref) => {
	const dispatch = useDispatch();
	const [overtimeId, setOvertimeId] = useState(null);
	const [visible, setVisible] = useState(false);
	const initialValues = useMemo(() => ({ rejectReason: "", remarks: "" }), []);
	//prettier-ignore
	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			rejectReason: yup.string().required(ERRORS.REQUIRED),
			/* FE rule - the backend only checks that the reason is non-blank */
			remarks: yup.string().when("rejectReason", { is: COMMON.OVERTIME.REJECT_REASON.OTHERS, then: (schema) => schema.required(ERRORS.REJECT_REMARKS_REQUIRED) }),
		}),
		onSubmit: (values) => {
			onHandleConfirm(values);
		},
	});

	const onHandleDismiss = useCallback(() => {
		setVisible(false);
	}, []);

	//prettier-ignore
	const onHandleShow = useCallback((id) => {
		setOvertimeId(id);
		formik.resetForm();
		setVisible(true);
	}, [formik]);

	//prettier-ignore
	const onHandleConfirm = useCallback(async (values) => {
		let response = null;

		try {
			await api.post.overtime.reject({ id: overtimeId, rejectReason: values.rejectReason, remarks: values.remarks });
			response = true;
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			formik.setSubmitting(false);
		}

		if (response) {
			onHandleDismiss();
			dispatch(promptAlertMessage({ message: "Overtime has been rejected successfully" }));
			props.onHandleGetList();
		}
	}, [dispatch, overtimeId, formik, onHandleDismiss, props]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	return (
		<Modal classes={{ root: "app-overtime-reject-reason-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="overtime-reject-reason-modal" aria-describedby="overtime-reject-reason-modal">
			<div className="overtime-reject-reason-modal">
				<div className="overtime-reject-reason-modal__main">
					<div className="overtime-reject-reason-modal__header">
						<p className="overtime-reject-reason-modal__title">Reason for Overtime Rejection</p>
						<p className="overtime-reject-reason-modal__description">Select a reason for rejecting this overtime request.</p>
					</div>

					<form className="overtime-reject-reason-modal__body" onSubmit={formik.handleSubmit}>
						{/* prettier-ignore */}
						<AppSelectInput required searchable={false} type="text" name="rejectReason" label="Reject Reason" placeholder="Please Select" options={COMMON.DROPDOWNS.OVERTIME_REJECT_REASON} value={formik.values.rejectReason} error={formik.errors.rejectReason} touched={formik.touched.rejectReason} disabled={formik.isSubmitting} onChange={formik.handleChange} />

						{/* prettier-ignore */}
						<AppInput required={formik.values.rejectReason === COMMON.OVERTIME.REJECT_REASON.OTHERS} multiline multilineRow={4} maxLength={255} type="text" name="remarks" label="Remarks" placeholder="Enter remarks" value={formik.values.remarks} error={formik.errors.remarks} touched={formik.touched.remarks} disabled={formik.isSubmitting} onChange={formik.handleChange} />

						<div className="overtime-reject-reason-modal__button-container">
							<AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
							<AppButton type="submit" label="Confirm" disabled={formik.isSubmitting} />
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppOvertimeRejectReasonModal));

AppOvertimeRejectReasonModal.propTypes = {
	ref: PropTypes.object,
	onHandleGetList: PropTypes.func,
};
